import React from "react";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import Section from "../components/marketing/Section";
import SectionHeading from "../components/marketing/SectionHeading";
import PriceCard from "../components/marketing/PriceCard";

/**
 * /pricing — rewritten for item 9 (2026-08-20). Replaces the old
 * $15/dev/mo single-plan model with what's actually live: Free (1 repo, 5
 * members, workload-only routing) and Pro ($99/mo flat, unlimited repos
 * and members, history-aware routing + team analytics).
 *
 * The old per-competitor comparison table is cut, not updated — a flat
 * team price doesn't translate into a single "$X/dev/mo" figure the way
 * per-seat pricing did, and fabricating a comparison wasn't part of this
 * item's pricing research. Revisit with real figures in a future pass if
 * a comparison is still wanted.
 *
 * FAQ reflects the real trial mechanics: no credit card required, and an
 * un-upgraded trial lands on Free automatically rather than losing access.
 */
const FAQS = [
  {
    q: "What's the difference between Free and Pro?",
    a: "Free covers 1 connected repo and up to 5 team members, with workload-based reviewer routing. Pro is $99/month flat — unlimited repos, unlimited team members, plus history-aware routing (we route to whoever's actually touched the code) and team analytics.",
  },
  {
    q: "How does the 14-day trial work?",
    a: "Every new team gets full Pro access for 14 days automatically — no credit card required to start. If you don't upgrade before the trial ends, you're moved to the Free plan, not locked out.",
  },
  {
    q: "Why is Pro priced flat instead of per seat?",
    a: "Anthurium's value is team-level — routing across your whole reviewer pool, team-wide analytics — so charging per invite would tax exactly the full-team adoption the product needs to work well. $99/month covers your whole team, however large it grows.",
  },
  {
    // The one place on the site that names competitors. Safe here in a way it
    // isn't in the hero: the question itself frames this as a comparison, so
    // naming them can't be misread as claiming an integration we don't have.
    // Deliberately no price comparison — the only competitor figures we hold
    // are unverified research notes, and they go stale.
    q: "Does this replace CodeRabbit or Copilot?",
    a: "No — it sits above them. Those tools review the contents of a diff. Anthurium decides which pull requests matter and who should own them, and never reads your code at all. Teams run both.",
  },
  {
    q: "What happens if we outgrow the Free plan?",
    // Item 24: was "Nothing is ever deleted." In context that only ever
    // meant downgrading, and that's still true — but as a bare absolute it
    // now sits one page away from a Delete Account button, and reads like a
    // promise being broken. Scoped to what it actually describes.
    a: "Downgrading never deletes anything. If you're over the Free limits — more than 1 repo or 5 members — you just choose which stay active until you upgrade; everything else is paused, not removed, and reverts automatically the moment you're on Pro.",
  },
];

export default function PricingPage() {
  return (
    <div className="overflow-x-hidden bg-rf-bg-primary">
      <MarketingNav />

      <Section className="!pb-8">
        <SectionHeading
          eyebrow="Pricing"
          title="Free to start. One flat price to grow."
          lead="$99/month covers your whole team — unlimited repos, unlimited members. Review tools that charge per developer get more expensive every time you hire; this one doesn't."
        />
      </Section>

      <Section className="!pt-8">
        <div className="flex flex-col items-center gap-6 lg:flex-row lg:items-stretch lg:justify-center">
          <PriceCard tier="free" />
          <PriceCard tier="pro" />
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-[68ch]">
          <h2 className="mb-8 text-2xl font-bold tracking-tight text-rf-text-primary">
            Frequently asked questions
          </h2>
          <dl className="flex flex-col gap-8">
            {FAQS.map((item) => (
              <div key={item.q}>
                <dt className="mb-2 text-base font-semibold text-rf-text-primary">{item.q}</dt>
                <dd className="text-sm leading-relaxed text-rf-text-secondary">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <MarketingFooter />
    </div>
  );
}
